import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { BpInput } from '../../../../shared/components/forms/input/BpInput'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { hideModal } from '../../../../app/store/reducers/AppSlice'
import { useAppDispatch } from '../../../../app/store/hooks/Redux'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { EMAIL_REGEX } from '../../../../shared/consts/RegularExpressions.const'
import {
  useGetFilterProjectsQuery,
  useGetFilterSubRolesQuery,
  useGetTeamManagementEditUserQuery,
  usePostTeamManagementEditUserMutation,
} from '../../../../app/api'
import { toProjectIds } from '../../model/to-project-ids'
import { useTranslation } from 'react-i18next'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import styles from './EditModal.module.scss'

export const EditModal = ({ id }: { id: number }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [editUser] = usePostTeamManagementEditUserMutation()
  const { data, isLoading, isFetching } = useGetTeamManagementEditUserQuery({ id })

  const {
    data: projects,
    isFetching: projectsFetching,
    isLoading: projectsLoading,
  } = useGetFilterProjectsQuery({ all: true })

  const {
    data: roles,
    isFetching: rolesFetching,
    isLoading: rolesLoading,
  } = useGetFilterSubRolesQuery()

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm<any>({
    mode: 'onChange',
    defaultValues: {},
  })

  const form = watch()

  const submit = (values: any) => {
    editUser({
      teamEditUserForm: {
        id,
        name: values.name,
        sub_role: values.sub_role,
        project_ids: toProjectIds(values.projects),
        auto_assign_new_projects: values.autoAssign,
      },
    }).then(() => dispatch(hideModal()))
  }

  useEffect(() => {
    if (data?.data) {
      reset({
        name: data.data.name,
        email: data.data.email,
        // sub_role: data.data.sub_role,
        projects: (data.data.project_ids ?? []).map((value: number) => String(value)),
        autoAssign: data.data.auto_assign_new_projects,
      })
    }
  }, [data])

  if (
    isLoading ||
    isFetching ||
    projectsLoading ||
    projectsFetching ||
    rolesLoading ||
    rolesFetching
  ) {
    return (
      <div className={styles.preloaderWrap}>
        <Preloader />
      </div>
    )
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(submit)}>
      <h3 className={styles.title}>{t('pages.team.inviteTeamMember')}</h3>
      <div className={styles.body}>
        <Controller
          render={({ field }) => (
            <BpInput
              {...field}
              required
              error={errors.name?.message as string}
              label={t('fields.assignName')}
              placeholder={t('fields.assignName')}
              {...register('name', {
                required: t('fields.validate.nameRequired'),
              })}
            />
          )}
          control={control}
          name='name'
        />
        <Controller
          render={({ field }) => (
            <BpInput
              {...field}
              disabled
              required
              error={errors.email?.message as string}
              label={t('fields.email')}
              placeholder={t('fields.email')}
              {...register('email', {
                required: t('fields.validate.emailRequired'),
                pattern: {
                  value: EMAIL_REGEX,
                  message: t('fields.validate.enterValidEmail'),
                },
              })}
            />
          )}
          control={control}
          name='email'
        />
        <Controller
          render={({ field }) => (
            <BpSelect
              dropPosition={'auto'}
              {...field}
              hasTranslate
              tooltip={
                <ul className={styles.tooltipList}>
                  <li>
                    <strong>{t('pages.team.roles.user')}:</strong> {t('pages.team.userDescription')}
                  </li>
                  <li>
                    <strong>{t('pages.team.roles.editor')}:</strong>{' '}
                    {t('pages.team.editorDescription')}
                  </li>
                  <li>
                    <strong>{t('pages.team.roles.admin')}:</strong>{' '}
                    {t('pages.team.adminDescription')}
                  </li>
                </ul>
              }
              disabled={!roles?.length}
              error={errors.sub_role?.message as string}
              label={t('fields.role')}
              loading={rolesLoading || rolesFetching}
              options={roles}
              placeholder={t('fields.role')}
              value={data?.data?.sub_role}
              {...register('sub_role', {
                required: t('fields.validate.required'),
              })}
            />
          )}
          control={control}
          name='sub_role'
        />
        {form.sub_role === 'user' && (
          <>
            <Controller
              render={({ field }) => (
                <BpSelect
                  dropPosition={'auto'}
                  {...field}
                  multiple
                  required
                  disabled={!projects?.length}
                  error={errors.projects?.message as string}
                  label={t('fields.projects')}
                  loading={projectsLoading || projectsFetching}
                  options={projects}
                  placeholder={t('fields.projects')}
                  {...register('projects', {
                    required: t('fields.validate.atLeastOneProject'),
                  })}
                />
              )}
              control={control}
              name='projects'
            />
            <div className={styles.checkboxRow}>
              <Controller
                render={({ field: { value, ...fieldProps } }) => (
                  <BpCheckbox
                    {...fieldProps}
                    checked={value}
                    defaultChecked={false}
                    label={t('fields.automaticallyAssign')}
                  />
                )}
                control={control}
                name={'autoAssign'}
              />
              <BpTooltip
                title={
                  'The invited user will be automatically assigned to every new project created in the workspace. This saves time and ensures they have immediate access to new projects without additional actions from your side.'
                }
              />
            </div>
          </>
        )}
      </div>
      <div className={styles.footer}>
        <BpButton className={styles.saveButton} disabled={!isValid} type='submit'>
          {t('buttons.save')}
        </BpButton>
        <BpButton theme='transparent' type='button' onClick={() => dispatch(hideModal())}>
          {t('buttons.cancel')}
        </BpButton>
      </div>
    </form>
  )
}
